import { KEYCLOAK_CLIENT, KEYCLOAK_REALM, KEYCLOAK_URL } from '@m/config/env';
import { AnyFunction } from '@m/types';
import { User } from '@m/types/api';
import { heapTrackProperties } from '@m/utils/heap';
import Keycloak, { KeycloakOnLoad } from 'keycloak-js';

let keycloakClient: Keycloak | null = null;

export const KeycloakService = {
  /**
   * Initializes Keycloak instance.
   *
   * @param onLoad
   * @param onAuthenticatedCallback
   * @param onInitComplete Called after init settles (success, error, or skipped on `/login-redirect`).
   */
  initKeycloak: async (
    onLoad: KeycloakOnLoad,
    onAuthenticatedCallback?: () => void,
    onInitComplete?: () => void,
  ) => {
    // Keycloak references window.document so wait until init is called to initialize.
    // TODO: Check is keycloak client can be moved out of the service object with keycloak v26.
    keycloakClient = new Keycloak({
      url: KEYCLOAK_URL,
      realm: KEYCLOAK_REALM,
      clientId: KEYCLOAK_CLIENT,
    });

    const finishInit = () => {
      onInitComplete?.();
    };

    // microsoft login hangs and then fails if we initialize keycloak. we don't need to initialize if we're in the login-redirect page
    if (window.location.pathname !== '/login-redirect') {
      keycloakClient
        .init({ onLoad })
        .then((authenticated) => {
          if (!authenticated) {
            // eslint-disable-next-line no-console
            console.log('User is not authenticated');
          }
          onAuthenticatedCallback?.();
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error(error);
        })
        .finally(() => {
          finishInit();
        });
    } else {
      onAuthenticatedCallback?.();
      finishInit();
    }
  },

  doLogin: () => {
    heapTrackProperties('Login', {
      login_method: 'keycloak',
    });
    return keycloakClient?.login();
  },

  isInit: () => !!keycloakClient,

  isTokenExpired: (minValidity = 60) => keycloakClient?.isTokenExpired(minValidity),

  doLogout: () => keycloakClient?.logout(),

  getToken: () => keycloakClient?.token,

  getTokenParsed: () => keycloakClient?.tokenParsed,

  isLoggedIn: () => !!keycloakClient?.token,

  updateToken: (successCallback: AnyFunction) =>
    keycloakClient?.updateToken(60).then(successCallback).catch(KeycloakService.doLogin),

  getUsername: () => keycloakClient!.tokenParsed?.preferred_username,

  getDisplayProfile: (): { name: string; subtitle?: string } => {
    if (!keycloakClient?.token) {
      return { name: 'User' };
    }
    const parsed = keycloakClient.tokenParsed;
    const username =
      typeof parsed?.preferred_username === 'string' ? parsed.preferred_username.trim() : '';
    const email = typeof parsed?.email === 'string' ? parsed.email.trim() : '';
    const first = typeof parsed?.given_name === 'string' ? parsed.given_name.trim() : '';
    const last = typeof parsed?.family_name === 'string' ? parsed.family_name.trim() : '';
    const fullName = [first, last].filter(Boolean).join(' ');
    const name = fullName || username || email || 'User';
    const subtitleRaw = email || username || undefined;
    const subtitle = subtitleRaw && subtitleRaw !== name ? subtitleRaw : undefined;
    return { name, subtitle };
  },

  // hasRole: (roles) => roles.some((role) => keycloakClient.hasRealmRole(role)),
  getUserObject: () =>
    ({
      id: keycloakClient?.tokenParsed?.sub,
      email: keycloakClient?.tokenParsed?.email,
      firstName: keycloakClient?.tokenParsed?.given_name,
      lastName: keycloakClient?.tokenParsed?.family_name,
    }) as Partial<User>,
};
